import {z} from 'zod';
import {toast} from 'react-toastify';
import React, {FC, useMemo, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {VerdocsSpinner} from '@verdocs/web-sdk-react';
import {FormProvider, useForm} from 'react-hook-form';
import {IProfile, TRole, updateOrganizationMember, VerdocsEndpoint} from '@verdocs/js-sdk';
import {getCallerAccess} from '../Views/Settings/Components/memberAccess';
import {Button, SelectInput, TextInput} from '../Components';
import {getHighestRole} from '../react-sdk/roles';
import {Dialog} from './Dialog';

interface FormValues {
  first_name: string;
  last_name: string;
  role_id: TRole;
}

// The API rejects blank names
const nameField = (current: string | null | undefined, message: string) =>
  current ? z.string().trim().min(1, message).max(100) : z.string().trim().max(100);

interface DialogProps {
  member: IProfile;
  onClose?: () => void;
}

export const EditMemberDialog: FC<DialogProps> = ({member, onClose = () => {}}) => {
  const [submitting, setSubmitting] = useState(false);
  const {assignableRoleOptions} = getCallerAccess();

  const highestRole = getHighestRole(member.roles);

  const schema = useMemo(
    () =>
      z.object({
        first_name: nameField(member.first_name, 'First name is required'),
        last_name: nameField(member.last_name, 'Last name is required'),
        role_id: z.enum(['owner', 'admin', 'member', 'basic_user', 'contact'], {error: 'Please select a role'}),
      }),
    [member.first_name, member.last_name],
  );

  const form = useForm<FormValues>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      role_id: highestRole,
    },
  });

  const onSubmit = async (data: FormValues) => {
    const {dirtyFields} = form.formState;
    const params: {first_name?: string; last_name?: string; roles?: TRole[]} = {};
    if (dirtyFields.first_name && data.first_name) params.first_name = data.first_name;
    if (dirtyFields.last_name && data.last_name) params.last_name = data.last_name;
    if (dirtyFields.role_id) params.roles = [data.role_id];

    if (!Object.keys(params).length) {
      onClose();
      return;
    }

    setSubmitting(true);
    await updateOrganizationMember(VerdocsEndpoint.getDefault(), member.id, params)
      .then(() => {
        toast.success('Member updated.');
        setSubmitting(false);
        onClose();
      })
      .catch((e: any) => {
        console.log('Error updating member', e);
        toast.error(e.response?.data?.error || 'Error updating member, please try again later.');
        setSubmitting(false);
      });
  };

  return (
    <Dialog onCancel={onClose}>
      <div className="mb-1 text-lg font-medium text-[#33364b]">
        Edit {[member.first_name, member.last_name].filter(Boolean).join(' ') || member.email}
      </div>
      <div className="mb-4 text-[13px] leading-normal text-gray-500">{member.email}</div>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <TextInput label="First name" name="first_name" required={!!member.first_name} />
          <TextInput label="Last name" name="last_name" required={!!member.last_name} />
          <SelectInput options={assignableRoleOptions} name="role_id" label="Role" />

          <div className="mt-5 flex flex-row items-center justify-end gap-2">
            {submitting && <VerdocsSpinner mode="dark" />}
            <Button type="button" size="small" variant="secondary" label="Cancel" onClick={onClose} disabled={submitting} />
            <Button size="small" label="Save" type="submit" disabled={submitting || !form.formState.isDirty} />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
